// ═══════════════════════════════════════════════════════════════════════════
// FILE: src/app/services/requests.service.ts
// DATE: 2026-10-12
// CREATOR: PIA (Rolle HUGO), beauftragt vom PO am 26.09.2026 / Auftrag 11.10.2026
// ceeq.it: _40 #223 Anfrage per @ (T2, T4)
//
// PURPOSE
//   Alles rund um die Anfrage per @ an einer Stelle:
//   ▸ HTTP: Ansprechbare je pslot, offene Anfragen, „Waiting for“, Schließen, Senden.
//   ▸ Zustand des Panels (Alt+P): offen, Reiter, hervorgehobene Anfrage.
//   ▸ Erinnerung: welche offene Anfrage ist jetzt wieder fällig?
//
// TAKT DER ERINNERUNG (F-3, Empfehlung vom 11.10.2026, als Konstanten unten)
//   erste Wiederkehr 2 min nach Eingang · danach alle 10 min · „Later“ = 60 min Ruhe ·
//   während der Nutzer in ein Feld tippt, wartet die Karte 30 s.
//   Die Fälligkeit liegt im Speicher und in localStorage — ein Neuladen setzt sie
//   nicht zurück, ein anderer Browser beginnt beim Eingang neu.
//
// ENTWURF (23.10.2026, Eisenhower-Fenster Alt+N)
//   openWithDraft() öffnet „Ask“ mit vorbefülltem pslot, Bezug, Empfänger und Text —
//   so wird aus „Delegate“ im Eisenhower-Fenster eine Anfrage mit einem Klick.
//
// BACKEND
//   GET  /api/pslot-addressees?pslot=     nodenanomvp/src/routes/pslot-addressees.route.js
//   GET  /api/requests/open               nodenanomvp/src/routes/message-requests.route.js
//   GET  /api/requests/waiting
//   POST /api/requests/:id/close          { reason: 'done' | 'obsolete' }
//   POST /api/discussion-posts?pslot=     Anfrage = Beitrag mit Appell und @-Nennung
// ═══════════════════════════════════════════════════════════════════════════

import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, catchError, firstValueFrom, map, of } from 'rxjs';

export interface Addressee {
  uuid: string;
  handle: string | null;
  display_name: string;
  is_agent: boolean;
  role_key: string | null;
  mentionable: boolean;
}

export interface OpenRequest {
  message_id: number;
  pslot: string;
  anchor: { type: string | null; id: number | null };
  post_id: number | null;
  thread_id: number | null;
  title: string;
  request_kind: 'request' | 'retro';
  from: { uuid: string; display_name: string; is_agent: boolean };
  cflx: { status_stmnt: string | null; imessage: string | null; appeal: string | null };
  reason: string;
  created_at: string;
}

export interface WaitingRequest {
  message_id: number;
  pslot: string;
  anchor: { type: string | null; id: number | null };
  post_id: number | null;
  title: string;
  request_kind: 'request' | 'retro';
  appeal: string | null;
  created_at: string;
  recipients: Array<{ uuid: string; display_name: string; is_agent: boolean;
    state: 'open' | 'closed'; close_reason: string | null }>;
}

export interface NewRequest {
  pslot: string;
  anchorType: string;
  anchorId: number;
  appeal: string;
  statusStmnt: string;
  imessage: string;
  retro: boolean;
}

export interface Reply {
  pslot: string;
  anchorType: string;
  anchorId: number;
  postId: number;
  questionRepeat: string;
  answer: string;
  nextStep: string;
}

export type PanelTab = 'ask' | 'mine' | 'waiting';

/** Vorbefüllte Anfrage (Eisenhower „Delegate“). to: Rolle (pia, nora …) oder uuid einer Person. */
export interface AskDraft {
  pslot: string;
  anchorType: string;
  anchorId: number;
  to: { role?: string; uuid?: string; label?: string };
  appeal: string;
  statusStmnt: string;
  imessage: string;
}

const ERSTE_WIEDERKEHR_MS = 2 * 60_000;
const TAKT_MS = 10 * 60_000;
const LATER_MS = 60 * 60_000;
const TIPP_PAUSE_MS = 30_000;
const POLL_MS = 60_000;
const STORE_KEY = 'ceeq.requests.nextDue';

@Injectable({ providedIn: 'root' })
export class RequestsService {
  private http = inject(HttpClient);
  private readonly api = this.buildApiBase();

  // ── Zustand des Panels ─────────────────────────────────────────────────
  readonly panelOpen = signal(false);
  readonly tab = signal<PanelTab>('ask');
  readonly focusId = signal<number | null>(null);
  /** Entwurf für den Reiter „Ask“ — das Panel übernimmt ihn beim Öffnen und leert ihn. */
  readonly draft = signal<AskDraft | null>(null);

  // ── Offene Anfragen und Erinnerung ─────────────────────────────────────
  readonly open = signal<OpenRequest[]>([]);
  readonly openCount = computed(() => this.open().length);
  /** Die Anfrage, deren Karte gerade am Rand steht. */
  readonly due = signal<OpenRequest | null>(null);
  /** false, solange die Migration fehlt (503) — dann bleibt alles still. */
  readonly available = signal(true);

  private nextDue = new Map<number, number>(this.ladeFaelligkeit());
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Gleiche Ableitung wie in den übrigen Diensten (messages.service.ts). */
  private buildApiBase(): string {
    const host = window.location.hostname;
    if (host.includes('localhost') || host === '127.0.0.1') return 'http://localhost:3000';
    return `https://api.${host}`;
  }

  // ── HTTP ───────────────────────────────────────────────────────────────

  addressees(pslot: string): Observable<Addressee[]> {
    const params = new HttpParams().set('pslot', pslot);
    return this.http
      .get<{ addressees: Addressee[] }>(`${this.api}/api/pslot-addressees`, { params, withCredentials: true })
      .pipe(map((r) => r.addressees || []), catchError((e) => {
        console.warn('[RequestsService] Ansprechbare nicht geladen pslot=%s status=%s', pslot, e?.status);
        return of([] as Addressee[]);
      }));
  }

  waiting(): Observable<WaitingRequest[]> {
    return this.http
      .get<{ requests: WaitingRequest[] }>(`${this.api}/api/requests/waiting`, { withCredentials: true })
      .pipe(map((r) => r.requests || []), catchError(() => of([] as WaitingRequest[])));
  }

  async send(r: NewRequest): Promise<number> {
    const params = new HttpParams().set('pslot', r.pslot);
    const body = {
      pslot: r.pslot,
      ref_anchor_type: r.anchorType,
      ref_anchor_id: r.anchorId,
      title: r.appeal.replace(/\s+/g, ' ').trim().slice(0, 120),
      cflx_status_stmnt: r.statusStmnt.trim(),
      cflx_imessage: r.imessage.trim(),
      cflx_appeal: r.appeal.trim(),
      request_kind: r.retro ? 'retro' : null,
      // discussion_posts.body ist auf devel NOT NULL (Schema-Abzug 09.10.2026) —
      // ohne body antwortet die Route mit 500. Leer ist erlaubt.
      body: '',
    };
    console.log('[RequestsService] Anfrage senden pslot=%s anker=%s#%s retro=%s', r.pslot, r.anchorType, r.anchorId, r.retro);
    const res = await firstValueFrom(this.http.post<{ post_id: number }>(
      `${this.api}/api/discussion-posts`, body, { params, withCredentials: true }));
    return res.post_id;
  }

  async reply(r: Reply): Promise<number> {
    const params = new HttpParams().set('pslot', r.pslot);
    const body = {
      pslot: r.pslot,
      ref_anchor_type: r.anchorType,
      ref_anchor_id: r.anchorId,
      ref_post_id: r.postId,
      title: 'Re: ' + r.questionRepeat.replace(/\s+/g, ' ').trim().slice(0, 110),
      cflx_questionrepeat: r.questionRepeat.trim(),
      cflx_imessage: r.answer.trim(),
      cflx_appeal: r.nextStep.trim(),
      body: '',
    };
    console.log('[RequestsService] Antwort senden pslot=%s post=%s', r.pslot, r.postId);
    const res = await firstValueFrom(this.http.post<{ post_id: number }>(
      `${this.api}/api/discussion-posts`, body, { params, withCredentials: true }));
    await this.refresh();
    return res.post_id;
  }

  async close(messageId: number, reason: 'done' | 'obsolete'): Promise<void> {
    console.log('[RequestsService] schließen message_id=%s reason=%s', messageId, reason);
    await firstValueFrom(this.http.post(`${this.api}/api/requests/${messageId}/close`,
      { reason }, { withCredentials: true }));
    this.nextDue.delete(messageId);
    this.speichereFaelligkeit();
    if (this.due()?.message_id === messageId) this.due.set(null);
    await this.refresh();
  }

  // ── Erinnerung ─────────────────────────────────────────────────────────

  /** Einmal aus der App-Hülle aufrufen. Wiederholte Aufrufe sind harmlos. */
  start(): void {
    if (this.timer) return;
    this.refresh();
    this.timer = setInterval(() => { this.refresh(); }, POLL_MS);
    // Fälligkeit öfter prüfen als laden: die Karte soll pünktlich kommen.
    setInterval(() => this.pruefeFaellig(), 15_000);
  }

  async refresh(): Promise<void> {
    if (!this.available()) return;
    try {
      const r = await firstValueFrom(this.http.get<{ requests: OpenRequest[] }>(
        `${this.api}/api/requests/open`, { withCredentials: true }));
      const liste = r.requests || [];
      this.open.set(liste);
      // Geschlossenes aus der Fälligkeit räumen.
      const ids = new Set(liste.map((q) => q.message_id));
      for (const id of [...this.nextDue.keys()]) if (!ids.has(id)) this.nextDue.delete(id);
      if (this.due() && !ids.has(this.due()!.message_id)) this.due.set(null);
      this.speichereFaelligkeit();
      this.pruefeFaellig();
    } catch (e: any) {
      if (e?.status === 503) {
        console.warn('[RequestsService] /api/requests meldet 503 — Migration fehlt, Erinnerung aus');
        this.available.set(false);
      } else if (e?.status !== 401) {
        console.warn('[RequestsService] offene Anfragen nicht geladen status=%s', e?.status);
      }
    }
  }

  /** Karte schließen ohne zu antworten: nächste Erinnerung im Takt. */
  dismiss(messageId: number): void { this.verschiebe(messageId, TAKT_MS); }

  /** „Later“: eine Stunde Ruhe für diese Anfrage. */
  later(messageId: number): void { this.verschiebe(messageId, LATER_MS); }

  private verschiebe(messageId: number, ms: number): void {
    this.nextDue.set(messageId, Date.now() + ms);
    this.speichereFaelligkeit();
    if (this.due()?.message_id === messageId) this.due.set(null);
  }

  private pruefeFaellig(): void {
    if (this.due() || this.panelOpen()) return;
    if (this.nutzerTippt()) return;
    const jetzt = Date.now();
    for (const q of this.open()) {
      if (!this.nextDue.has(q.message_id)) {
        const eingang = Date.parse(q.created_at) || jetzt;
        this.nextDue.set(q.message_id, eingang + ERSTE_WIEDERKEHR_MS);
      }
      if (this.nextDue.get(q.message_id)! <= jetzt) {
        console.log('[RequestsService] 🔔 Erinnerung message_id=%s', q.message_id);
        this.due.set(q);
        this.nextDue.set(q.message_id, jetzt + TAKT_MS);
        this.speichereFaelligkeit();
        return;
      }
    }
    this.speichereFaelligkeit();
  }

  private nutzerTippt(): boolean {
    const el = document.activeElement as HTMLElement | null;
    const tippt = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
    if (tippt) {
      for (const [id, t] of this.nextDue) {
        if (t <= Date.now()) this.nextDue.set(id, Date.now() + TIPP_PAUSE_MS);
      }
    }
    return tippt;
  }

  private ladeFaelligkeit(): Array<[number, number]> {
    try {
      const roh = localStorage.getItem(STORE_KEY);
      return roh ? (JSON.parse(roh) as Array<[number, number]>) : [];
    } catch { return []; }
  }

  private speichereFaelligkeit(): void {
    try { localStorage.setItem(STORE_KEY, JSON.stringify([...this.nextDue])); } catch { /* privat */ }
  }

  // ── Panel ──────────────────────────────────────────────────────────────

  openPanel(tab: PanelTab = 'ask', focusId: number | null = null): void {
    this.tab.set(tab);
    this.focusId.set(focusId);
    this.panelOpen.set(true);
    if (focusId !== null && this.due()?.message_id === focusId) this.due.set(null);
  }

  closePanel(): void { this.panelOpen.set(false); this.focusId.set(null); }

  openWithDraft(d: AskDraft): void {
    console.log('[RequestsService] Entwurf öffnen pslot=%s anker=%s#%s an=%s', d.pslot, d.anchorType, d.anchorId, d.to.role || d.to.uuid);
    this.draft.set(d);
    if (this.panelOpen() && this.tab() === 'ask') { this.panelOpen.set(false); queueMicrotask(() => this.openPanel('ask')); }
    else this.openPanel('ask');
  }
}

/** Wohin ein Bezug führt. Gleiche Pfade wie app.routes.ts. */
export function anchorLink(pslot: string, type: string | null, id: number | null): string | null {
  if (!pslot || !type) return null;
  switch (type) {
    case 'item': return `/project/${pslot}/item/${id}`;
    case 'task': return `/project/${pslot}/task/${id}/edit`;
    case 'sprint': return `/project/${pslot}/sprint/${id}`;
    case 'milestone': return `/project/${pslot}/milestone/${id}/edit`;
    case 'pslot': return `/project/${pslot}/dashboard`;
    default: return null;
  }
}

/** Alter in Worten, für Karte und Listen. */
export function ageOf(iso: string): string {
  const t = Date.parse(iso);
  if (isNaN(t)) return 'date unknown';
  const min = Math.round((Date.now() - t) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}
